/**
 * Question equivalence map — "asked only once" (CEO benchmark, Fri Oct 9 test).
 *
 * The no-repeat rule was per question id, not per fact: an answer is stored as
 * `user_knowledge.attribute_key = 'survey_qm_' || question_catalog.id`, and
 * every renderer decided "already answered" by looking for that exact key.
 * Two catalog rows that ask the same thing are two different keys, so
 * answering one never suppressed the other — plus the legacy flat keys
 * (`postal_code`, `first_name`, …) and `profiles.email` / `phone_number`
 * were invisible to the renderer entirely.
 *
 * This module is the single explicit map: a question counts as answered when
 * the member holds ANY equivalent answer. Group contents are mirrored
 * byte-for-byte in `supabase/functions/_shared/question-equivalence.ts`
 * (Deno-importable server copy) and in reklaim-services
 * `packages/supabase/consumer/question-equivalence.ts` (+ JSON doc).
 * Keep all three identical — the PR states this.
 *
 * Full UUIDs resolved from staging `question_catalog` (Oct 8 2026):
 * - Zip Code `4259747c-e9d9-43e1-9bf9-42568d36c3fd`
 * - Last name `83b01edf-8757-4417-b773-16ef941cbdad`
 * - First name `26dc4d83-f640-47b9-b0af-084b20291013`
 * - Date of Birth `0e7776ad-835f-4263-ac12-a4cf9abbe80f`
 * - State `a30e811b-ac69-4d58-aa86-075054832e26`
 * - Age range `89daea42-d791-4ac4-9d78-1c40c316120b`
 * - Blue origin zip `8055b437-63bd-4f91-9c53-ca6250cf8c0b`
 * - Blue first/last `e43736f5-75ea-490d-adb8-4ef5d3b46c66` /
 *   `8fd91e6d-dd4b-43e1-9996-d28c45366445`
 * - Blue email/phone `c552d986-6b81-4c81-8cb1-73a1bfd9649b` /
 *   `52ccbf93-0c07-459b-a941-a59c5740d8a2`
 *
 * Gated behind `surveys.equivalent_answers` (default OFF, fail-closed).
 */

export const EQUIVALENT_ANSWERS_FLAG_KEY = 'surveys.equivalent_answers';
export const EQUIVALENT_ANSWERS_FLAG_CATEGORY = 'surveys';

// ── Canonical question ids (full UUIDs) ──

export const ZIP_CANONICAL_ID = '4259747c-e9d9-43e1-9bf9-42568d36c3fd';
export const ZIP_ORIGIN_ID = '8055b437-63bd-4f91-9c53-ca6250cf8c0b';
export const LAST_NAME_CANONICAL_ID = '83b01edf-8757-4417-b773-16ef941cbdad';
export const LAST_NAME_WIZARD_ID = '8fd91e6d-dd4b-43e1-9996-d28c45366445';
export const FIRST_NAME_CANONICAL_ID = '26dc4d83-f640-47b9-b0af-084b20291013';
export const FIRST_NAME_WIZARD_ID = 'e43736f5-75ea-490d-adb8-4ef5d3b46c66';
export const DOB_CANONICAL_ID = '0e7776ad-835f-4263-ac12-a4cf9abbe80f';
export const AGE_RANGE_QUESTION_ID = '89daea42-d791-4ac4-9d78-1c40c316120b';
export const STATE_CANONICAL_ID = 'a30e811b-ac69-4d58-aa86-075054832e26';
export const EMAIL_QUESTION_ID = 'c552d986-6b81-4c81-8cb1-73a1bfd9649b';
export const PHONE_QUESTION_ID = '52ccbf93-0c07-459b-a941-a59c5740d8a2';

// ── Legacy flat `user_knowledge` keys (written by lemonade_wizard) ──

export const FLAT_POSTAL_CODE = 'postal_code';
export const FLAT_LAST_NAME = 'last_name';
export const FLAT_FIRST_NAME = 'first_name';
export const FLAT_DATE_OF_BIRTH = 'date_of_birth';
export const FLAT_STATE = 'state';

export const EQUIVALENCE_FLAT_KEYS: readonly string[] = [
  FLAT_POSTAL_CODE,
  FLAT_LAST_NAME,
  FLAT_FIRST_NAME,
  FLAT_DATE_OF_BIRTH,
  FLAT_STATE,
];

export interface EquivalenceGroup {
  /** Question ids that ask the same fact — bidirectional within the group. */
  readonly questionIds: readonly string[];
  /** Legacy flat `user_knowledge` keys that hold the same fact. */
  readonly flatKeys: readonly string[];
}

export const EQUIVALENCE_GROUPS: readonly EquivalenceGroup[] = [
  { questionIds: [ZIP_CANONICAL_ID, ZIP_ORIGIN_ID], flatKeys: [FLAT_POSTAL_CODE] },
  { questionIds: [LAST_NAME_CANONICAL_ID, LAST_NAME_WIZARD_ID], flatKeys: [FLAT_LAST_NAME] },
  { questionIds: [FIRST_NAME_CANONICAL_ID, FIRST_NAME_WIZARD_ID], flatKeys: [FLAT_FIRST_NAME] },
  { questionIds: [DOB_CANONICAL_ID], flatKeys: [FLAT_DATE_OF_BIRTH] },
  { questionIds: [STATE_CANONICAL_ID], flatKeys: [FLAT_STATE] },
];

export interface EquivalenceSnapshot {
  /** Answered `survey_qm_*` mapping ids (raw question ids, prefix stripped). */
  readonly answeredIds: ReadonlySet<string>;
  /** Present legacy flat `user_knowledge` keys (non-empty answers only). */
  readonly flatKeys: ReadonlySet<string>;
  /** Non-empty `profiles.email` suppresses the Blue email question. */
  readonly hasProfileEmail: boolean;
  /** Non-empty `profiles.phone_number` suppresses the Blue phone question. */
  readonly hasProfilePhone: boolean;
}

/**
 * Catalog slice for generic `profile_attribute_key` matching (contract v3
 * `matchBy`): raw question id (uuid, no `survey_qm_` prefix) → its
 * `question_catalog.profile_attribute_key` (null when unset). Built by the
 * read layer from already-loaded catalog rows plus a fill for answered ids
 * the rendered set does not cover, so no duplicate id needs a contract
 * change. Entries with null/blank keys never match — they stay covered by
 * the explicit groups above. Null when the caller has no catalog (groups
 * and implications still apply).
 */
export interface EquivalenceCatalog {
  readonly questionProfileKeys: ReadonlyMap<string, string | null>;
}

/** Canonical fact name: trimmed + lowercased, never empty. */
export function normalizeProfileFact(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const fact = value.trim().toLowerCase();
  return fact.length > 0 ? fact : null;
}

const KNOWLEDGE_PREFIX = 'survey_qm_';

const knowledgeKey = (questionId: string): string => `${KNOWLEDGE_PREFIX}${questionId}`;

function rawId(key: string): string {
  return key.startsWith(KNOWLEDGE_PREFIX) ? key.slice(KNOWLEDGE_PREFIX.length) : key;
}

/** Explicit groups in combined key form (`survey_qm_<uuid>` + bare flat keys). */
const COMBINED_GROUPS: readonly (readonly string[])[] = EQUIVALENCE_GROUPS.map((g) => [
  ...g.questionIds.map(knowledgeKey),
  ...g.flatKeys,
]);

function holderOf(answered: ReadonlySet<string>, group: readonly string[]): boolean {
  return group.some((member) => answered.has(member) || answered.has(rawId(member)));
}

function addWithRaw(target: Set<string>, member: string): boolean {
  let grew = false;
  if (!target.has(member)) {
    target.add(member);
    grew = true;
  }
  const raw = rawId(member);
  if (!target.has(raw)) {
    target.add(raw);
    grew = true;
  }
  return grew;
}

/**
 * Canonical date-of-birth fact: the flat DOB key is already canonical
 * (lowercase, no whitespace), so it doubles as the normalized fact name
 * the profile-key arm compares against.
 */
const DATE_OF_BIRTH_FACT: string = FLAT_DATE_OF_BIRTH;

/**
 * Facts the answered set holds: for each answered id, the normalized
 * `profile_attribute_key` from the catalog (when the row carries a
 * non-blank one) plus the answered id itself as its own fact — that last
 * arm is what lets a legacy flat key (`postal_code`, …) satisfy a catalog
 * row carrying the same key. Null-keyed catalog rows contribute nothing,
 * so they stay covered by the explicit groups only.
 */
function collectProfileFacts(
  expanded: ReadonlySet<string>,
  catalog: EquivalenceCatalog,
): Set<string> {
  const facts = new Set<string>();
  for (const id of expanded) {
    const raw = rawId(id);
    const keyed = normalizeProfileFact(catalog.questionProfileKeys.get(raw) ?? null);
    if (keyed) facts.add(keyed);
    const self = normalizeProfileFact(raw);
    if (self) facts.add(self);
  }
  return facts;
}

/** A normalized catalog fact counts only when the answered set holds it. */
function factHeld(facts: ReadonlySet<string>, fact: string | null): boolean {
  return fact !== null && facts.has(fact);
}

/**
 * Profile-key growth: every catalog row whose non-blank key names a held
 * fact joins the answered set (both key forms). Rows with a null/blank key
 * never match.
 */
function growProfileKeyMatches(
  expanded: Set<string>,
  facts: ReadonlySet<string>,
  catalog: EquivalenceCatalog,
): boolean {
  if (facts.size === 0) return false;
  let grown = false;
  for (const [questionId, key] of catalog.questionProfileKeys) {
    if (factHeld(facts, normalizeProfileFact(key))) {
      grown = addWithRaw(expanded, knowledgeKey(questionId)) || grown;
    }
  }
  return grown;
}

/**
 * Date-of-birth implication growth: the implication fires through the
 * explicit DOB members AND the generic fact arm, so a duplicate DOB id the
 * groups never listed still implies the age range once its `date_of_birth`
 * fact is held.
 */
function growDateOfBirthImplication(expanded: Set<string>, facts: ReadonlySet<string> | null): boolean {
  const dobHeld =
    holderOf(expanded, [knowledgeKey(DOB_CANONICAL_ID), FLAT_DATE_OF_BIRTH]) ||
    (facts?.has(DATE_OF_BIRTH_FACT) ?? false);
  if (!dobHeld) return false;
  return addWithRaw(expanded, knowledgeKey(AGE_RANGE_QUESTION_ID));
}

function growContactSuppression(expanded: Set<string>, snapshot: EquivalenceSnapshot): boolean {
  let grown = false;
  if (snapshot.hasProfileEmail) {
    grown = addWithRaw(expanded, knowledgeKey(EMAIL_QUESTION_ID)) || grown;
  }
  if (snapshot.hasProfilePhone) {
    grown = addWithRaw(expanded, knowledgeKey(PHONE_QUESTION_ID)) || grown;
  }
  return grown;
}

function growAnsweredOnce(
  expanded: Set<string>,
  snapshot: EquivalenceSnapshot,
  catalog: EquivalenceCatalog | null | undefined,
): boolean {
  let grown = false;
  for (const group of COMBINED_GROUPS) {
    if (!holderOf(expanded, group)) continue;
    for (const member of group) {
      grown = addWithRaw(expanded, member) || grown;
    }
  }
  const facts = catalog == null ? null : collectProfileFacts(expanded, catalog);
  grown = growDateOfBirthImplication(expanded, facts) || grown;
  grown = growContactSuppression(expanded, snapshot) || grown;
  if (facts !== null && catalog != null) {
    grown = growProfileKeyMatches(expanded, facts, catalog) || grown;
  }
  return grown;
}

/**
 * Expand a snapshot to the combined satisfied set (fixpoint). The working
 * set holds both key forms (`survey_qm_<uuid>` + raw ids + bare flat keys)
 * so lookups succeed whichever form a caller holds. Only the satisfied set
 * grows — selections stay keyed by the question actually answered, so this
 * can never fabricate an answer row.
 *
 * Without a catalog this is exactly the explicit groups + DOB implication
 * + contact suppression. With a catalog it additionally matches by
 * `profile_attribute_key` (contract v3 `matchBy`).
 */
export function expandSatisfiedIds(
  snapshot: EquivalenceSnapshot,
  catalog?: EquivalenceCatalog | null,
): Set<string> {
  const expanded = new Set<string>();
  for (const id of snapshot.answeredIds) addWithRaw(expanded, knowledgeKey(rawId(id)));
  for (const key of snapshot.flatKeys) {
    if (!expanded.has(key)) expanded.add(key);
  }
  while (growAnsweredOnce(expanded, snapshot, catalog ?? null)) {
    // Fixpoint: every growth arm only adds members, so this settles once
    // nothing new matches.
  }
  return expanded;
}

function groupFor(questionId: string): EquivalenceGroup | undefined {
  return EQUIVALENCE_GROUPS.find((g) => g.questionIds.includes(questionId));
}

/**
 * True when the member holds ANY equivalent answer for `questionId`:
 * the question's own answer, a bidirectional group sibling (question or
 * flat key), `profiles.email` / `phone_number` for the Blue contact
 * questions, or a known DOB for the age-range question. With a catalog,
 * additionally any catalog row sharing the held `profile_attribute_key`
 * fact (contract v3 `matchBy` — covers duplicate ids the explicit groups
 * never listed; null/blank-keyed rows stay id-only).
 *
 * Directional exceptions (NOT bidirectional):
 * - DOB present satisfies age range `89daea42`; an age-range answer does
 *   NOT satisfy DOB.
 * - `profiles.email` / `phone_number` suppress the Blue questions; answering
 *   the Blue question does not write the profile.
 */
export function isQuestionSatisfied(
  questionId: string,
  snapshot: EquivalenceSnapshot,
  catalog?: EquivalenceCatalog | null,
): boolean {
  const expanded = expandSatisfiedIds(snapshot, catalog ?? null);
  return expanded.has(questionId) || expanded.has(knowledgeKey(questionId));
}

/** Subset of `questionIds` NOT satisfied by any equivalent answer. */
export function filterUnansweredByEquivalence(
  questionIds: readonly string[],
  snapshot: EquivalenceSnapshot,
): string[] {
  return questionIds.filter((id) => !isQuestionSatisfied(id, snapshot));
}

/**
 * Duplicate date-of-birth answer (contract v3 `matchBy`, flag-on path):
 * the first held answer whose catalog key names the `date_of_birth` fact,
 * excluding the canonical DOB id the caller already checked. Lets an
 * unlisted duplicate DOB derive the age range exactly like the canonical
 * one. Keyed rows only — null/blank keys never match.
 */
function duplicateDobAnswer(
  answersByMappingId: ReadonlyMap<string, unknown>,
  catalog: EquivalenceCatalog,
): unknown {
  for (const [id, v] of answersByMappingId) {
    if (v === undefined || id === DOB_CANONICAL_ID) continue;
    if (normalizeProfileFact(catalog.questionProfileKeys.get(id)) === DATE_OF_BIRTH_FACT) {
      return v;
    }
  }
  return undefined;
}

/**
 * Effective answer for gating/validation: the question's own selection, else
 * the first available equivalent sibling's selection (group questions, then
 * flat keys). Directionally, a known DOB resolves the age-range question to
 * its derived option in STORED form (the catalog option id when options
 * carry explicit ids, else the label — identical to what answering the
 * question would store — never the raw DOB string). Returns undefined when
 * nothing in the group was answered, when age options are missing, or when
 * the DOB cannot be mapped to a configured option.
 */
export function getEffectiveAnswer(
  questionId: string,
  answersByMappingId: ReadonlyMap<string, unknown>,
  flatAnswersByKey?: ReadonlyMap<string, unknown>,
  ageRangeOptions?: AgeRangeOptions,
  /**
   * Catalog slice for generic `profile_attribute_key` matching (contract
   * v3 `matchBy`, flag-on path): when the targeted question holds no
   * explicit-group equivalent, resolve through any held answer sharing its
   * normalized fact. Selections stay keyed by the question actually
   * answered — this only reads them for gating/targeting.
   */
  catalog?: EquivalenceCatalog | null,
): unknown {
  const direct = answersByMappingId.get(questionId);
  if (direct !== undefined) return direct;
  if (questionId === AGE_RANGE_QUESTION_ID) {
    const dob =
      answersByMappingId.get(DOB_CANONICAL_ID) ??
      flatAnswersByKey?.get(FLAT_DATE_OF_BIRTH) ??
      (catalog ? duplicateDobAnswer(answersByMappingId, catalog) : undefined);
    if (dob !== undefined) return deriveAgeRangeLabel(dob, ageRangeOptions);
    // No DOB held: without a catalog this resolves nothing (as before);
    // with one, fall through so an unlisted duplicate AGE answer sharing
    // the age fact still resolves through the generic path below instead
    // of hiding age-gated children while `isQuestionSatisfied` hides age.
    if (!catalog) return undefined;
  }
  const group = groupFor(questionId);
  if (group) {
    for (const sibling of group.questionIds) {
      if (sibling === questionId) continue;
      const v = answersByMappingId.get(sibling);
      if (v !== undefined) return v;
    }
    if (flatAnswersByKey) {
      for (const key of group.flatKeys) {
        const v = flatAnswersByKey.get(key);
        if (v !== undefined) return v;
      }
    }
  }
  if (catalog) {
    const fact =
      normalizeProfileFact(catalog.questionProfileKeys.get(questionId)) ??
      normalizeProfileFact(questionId);
    if (fact) {
      for (const [id, v] of answersByMappingId) {
        if (v === undefined) continue;
        const idFact =
          normalizeProfileFact(catalog.questionProfileKeys.get(id)) ?? normalizeProfileFact(id);
        if (idFact === fact) return v;
      }
      if (flatAnswersByKey) {
        for (const [key, v] of flatAnswersByKey) {
          if (v !== undefined && normalizeProfileFact(key) === fact) return v;
        }
      }
    }
  }
  return undefined;
}

/**
 * Whole-year age for a stored DOB value (ISO string, epoch millis, or Date).
 * Returns undefined for missing/unparseable/future/absurd values so callers
 * fail safe (keep asking) instead of matching a wrong range.
 *
 * Date-only strings (`YYYY-MM-DD`, the lemon/wizard DOB writer form) are
 * calendar dates with no time: they parse as UTC midnight and compare in UTC.
 * Comparing them in local time would shift west-of-UTC members a calendar
 * day and mis-age them on the birthday boundary.
 */
export function computeAgeFromDob(dobValue: unknown, nowMs: number = Date.now()): number | undefined {
  let date: Date | undefined;
  let dateOnly = false;
  if (dobValue instanceof Date) {
    date = dobValue;
  } else if (typeof dobValue === 'number') {
    const parsed = new Date(dobValue);
    if (!Number.isNaN(parsed.getTime())) date = parsed;
  } else if (typeof dobValue === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dobValue.trim());
    if (m) {
      const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      const parsed = new Date(ms);
      if (!Number.isNaN(parsed.getTime())) {
        date = parsed;
        dateOnly = true;
      }
    } else {
      const parsed = new Date(dobValue);
      if (!Number.isNaN(parsed.getTime())) date = parsed;
    }
  }
  if (!date) return undefined;
  const now = new Date(nowMs);
  const y = dateOnly ? now.getUTCFullYear() - date.getUTCFullYear() : now.getFullYear() - date.getFullYear();
  const nowM = dateOnly ? now.getUTCMonth() : now.getMonth();
  const dobM = dateOnly ? date.getUTCMonth() : date.getMonth();
  const nowD = dateOnly ? now.getUTCDate() : now.getDate();
  const dobD = dateOnly ? date.getUTCDate() : date.getDate();
  let age = y;
  if (nowM < dobM || (nowM === dobM && nowD < dobD)) age -= 1;
  if (age < 0 || age > 130) return undefined;
  return age;
}

interface AgeRange {
  min: number;
  max: number;
}

/**
 * Parse a configured age-range option label into an inclusive [min, max].
 * Handles the catalog patterns `N-M`, `N+`, `Under N`, `N or older`,
 * `Over N` (case-insensitive, trailing `years`/`yrs`/`old` tolerated).
 * Returns null for anything else — the option simply never matches.
 */
function parseAgeRangeLabel(label: string): AgeRange | null {
  const cleaned = label
    .trim()
    .replace(/\s*(years?\s*old|years?|yrs?|yo)\s*$/i, '')
    .trim();
  const span = cleaned.match(/^(\d+)\s*[-–]\s*(\d+)$/);
  if (span) {
    const min = Number(span[1]);
    const max = Number(span[2]);
    if (min <= max) return { min, max };
    return null;
  }
  const plus = cleaned.match(/^(\d+)\s*\+$/);
  if (plus) return { min: Number(plus[1]), max: 130 };
  const under = cleaned.match(/^under\s+(\d+)$/i);
  if (under) {
    const max = Number(under[1]) - 1;
    if (max >= 0) return { min: 0, max };
    return null;
  }
  const older = cleaned.match(/^(\d+)\s+or\s+older$/i);
  if (older) return { min: Number(older[1]), max: 130 };
  const over = cleaned.match(/^over\s+(\d+)$/i);
  if (over) return { min: Number(over[1]), max: 130 };
  return null;
}

/**
 * A configured age-range option in stored form. Bare labels (the legacy
 * `readonly string[]` call shape) store verbatim; `{ id, text }` pairs store
 * the option id — the same value answering the question would persist — so
 * a DOB-derived answer compares exactly like a directly-answered one
 * against `depends_on_values` / `target_values`.
 */
export interface AgeRangeOption {
  readonly id: string;
  readonly text: string;
}

export type AgeRangeOptions = readonly (string | AgeRangeOption)[];

/**
 * Map a DOB to the configured age-range option whose range contains the
 * member's age (first match in option order wins). Returns the option in
 * STORED form (option id when the entry carries one, else the label
 * verbatim) — never the raw DOB. Returns undefined when options are
 * missing, the DOB is unusable, or no range contains the age: the caller
 * then keeps asking the age question (safe) instead of storing a raw DOB
 * where a range value belongs.
 */
export function deriveAgeRangeLabel(
  dobValue: unknown,
  options: AgeRangeOptions | undefined,
  nowMs: number = Date.now(),
): string | undefined {
  if (!options || options.length === 0) return undefined;
  const age = computeAgeFromDob(dobValue, nowMs);
  if (age === undefined) return undefined;
  for (const raw of options) {
    const text = typeof raw === 'string' ? raw : raw.text;
    if (typeof text !== 'string') continue;
    const range = parseAgeRangeLabel(text);
    if (!range) continue;
    if (age >= range.min && age <= range.max) {
      return typeof raw === 'string' ? raw : raw.id;
    }
  }
  return undefined;
}

/**
 * Fail-closed async flag read for surfaces that cannot use the
 * `useEquivalentAnswersEnabled` hook (profile wizard loader, progress
 * query). Missing row, disabled row, or ANY error reads as OFF.
 *
 * Takes a pre-bound read instead of the Supabase client: pushing the full
 * generic client through a structural parameter trips TS2589
 * ("instantiation is excessively deep") on the Database instantiation,
 * while a concrete `.maybeSingle()` chain checks cheaply.
 */
export async function fetchEquivalentAnswersEnabled(
  readFlag: () => PromiseLike<{ data: { enabled?: unknown } | null }>,
): Promise<boolean> {
  try {
    const { data } = await readFlag();
    return data?.enabled === true;
  } catch {
    return false;
  }
}
